import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isLoggedIn, hasFeature, getMyUser, isAdministrator } from '../store/session/selectors';
import { areSettingsLoaded, getSettings } from '../store/settings/selectors';
import { FeatureEnum } from '../models';

const TAWK_SCRIPT_ID = 'tawk-script';
const TAWK_SRC = 'https://embed.tawk.to/693a0c878465ad197f9bc88c/1jc5c1snf';
const PENDING_ATTRS_KEY = '__appointmintTawkPendingAttrs';

const setAttributesSafe = (attrs: { name: string; email: string }) => {
    const api = (window as any).Tawk_API;
    if (!api?.setAttributes) {
        return false;
    }

    api.setAttributes(attrs, function(error: any) {
        if (error) {
            console.error('Error setting Tawk.to user attributes:', error);
        }
    });

    return true;
};

export const TawkChat = () => {
    const loggedIn = useSelector(isLoggedIn);
    const hasSupportChat = useSelector(hasFeature(FeatureEnum.SupportChat));
    const settingsLoaded = useSelector(areSettingsLoaded);
    const myUser = useSelector(getMyUser);
    const admin = useSelector(isAdministrator);
    const settings = useSelector(getSettings);

    useEffect(() => {
        if (!loggedIn || !hasSupportChat || !settingsLoaded) {
            const api = (window as any).Tawk_API;
            api?.hideWidget?.();
            return;
        }

        // Only inject script once - if it already exists, Tawk will handle reinitialization
        const existingScript = document.getElementById(TAWK_SCRIPT_ID);
        if (existingScript) {
            const api = (window as any).Tawk_API;
            api?.showWidget?.();
            return;
        }

        // Initialize Tawk_API before script loads
        (window as any).Tawk_API = (window as any).Tawk_API || {};
        (window as any).Tawk_LoadStart = new Date();

        const api = (window as any).Tawk_API;

        // Set up onLoad handler
        api.onLoad = function() {
            const pending = api[PENDING_ATTRS_KEY];
            if (pending) {
                setAttributesSafe(pending);
                delete api[PENDING_ATTRS_KEY];
            }
        };

        const script = document.createElement('script');
        script.id = TAWK_SCRIPT_ID;
        script.async = true;
        script.src = TAWK_SRC;
        script.charset = 'UTF-8';
        script.setAttribute('crossorigin', '*');
        script.onerror = () => {
            console.error('Failed to load Tawk.to script');
        };

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.head.appendChild(script);
        }
    }, [loggedIn, hasSupportChat, settingsLoaded]);

    useEffect(() => {
        if (!loggedIn || !hasSupportChat || !settingsLoaded || !myUser) {
            return;
        }

        const role = admin ? 'Admin' : 'Professional';
        const companyName = settings?.general?.companyName;
        const displayName = companyName
            ? `[${companyName}] ${myUser.username} (${role})`
            : `${myUser.username} (${role})`;
        const attrs = { name: displayName, email: myUser.email || '' };
        if (!setAttributesSafe(attrs)) {
            const api = (window as any).Tawk_API;
            if (api) {
                api[PENDING_ATTRS_KEY] = attrs;
                api.visitor = attrs;
            }
        }
    }, [loggedIn, hasSupportChat, settingsLoaded, myUser, admin, settings]);

    return null;
};
